import type { ApiError } from "./types";

export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === "object" &&
    value !== null &&
    "status" in value &&
    "code" in value &&
    "message" in value
  );
}

export function mapEdenError(error: unknown): ApiError {
  if (isApiError(error)) return error;
  if (error && typeof error === "object" && "status" in error && typeof (error as { status: unknown }).status === "number") {
    const status = (error as { status: number }).status;
    const value = (error as { value?: unknown }).value;
    // API error envelope: { error: { code, message, details? } }
    if (
      value &&
      typeof value === "object" &&
      "error" in value &&
      value.error &&
      typeof value.error === "object" &&
      "code" in (value.error as object) &&
      "message" in (value.error as object)
    ) {
      const inner = value.error as { code: unknown; message: unknown; details?: unknown };
      return {
        status,
        code: String(inner.code),
        message: String(inner.message),
        details: inner.details,
      };
    }
    // Flat shape: { code, message }
    if (value && typeof value === "object" && "code" in value && "message" in value) {
      return {
        status,
        code: String((value as { code: unknown }).code),
        message: String((value as { message: unknown }).message),
        details: value,
      };
    }
    return {
      status,
      code: `http_${status}`,
      message: typeof value === "string" ? value : `HTTP ${status}`,
      details: value,
    };
  }
  if (error instanceof Error) {
    return { status: 0, code: "network_error", message: error.message };
  }
  return { status: 0, code: "unknown_error", message: "Unknown error" };
}

/**
 * Coerce anything thrown by a request into a sentence a customer can read.
 *
 * Eden's `EdenFetchError` extends `Error` and builds its message with
 * `super(value + "")`, so for the JSON body every API error actually carries,
 * `.message` is the literal string "[object Object]". Any `e instanceof Error`
 * branch therefore renders that straight into the UI — which is exactly what a
 * customer saw instead of the reason their booking was rejected. Read the
 * envelope first and only fall back to `.message` when it says something.
 */
export function errorMessage(error: unknown, fallback: string): string {
  const found = readMessage(error);
  if (!found || isDeveloperText(found.message, found.code)) return fallback;
  return found.message;
}

/**
 * Codes whose message is written for us, not the traveller. The API's error
 * middleware fills these with "validation failed", "route not found" or, for a
 * failed body schema, the whole validation report as JSON, which a 422 once
 * put in the AI chat verbatim (owner screenshot, 2026-09-25). Business codes
 * like `not_modifiable` carry a sentence meant to be read and still pass.
 */
const DEVELOPER_CODES = new Set([
  "validation_failed",
  "not_found",
  "invalid_input",
  "internal",
  "internal_error",
  "network_error",
  "unknown_error",
]);

/** Status lines, JSON bodies and the browsers' own fetch failures. */
const DEVELOPER_TEXT = [
  /^\s*[[{]/,
  /^HTTP \d{3}/i,
  /^[\w/.-]+ \d{3}:/, // "ai/chat/stream 422: …"
  /^\d{3}\b/, // "401 unauthorized"
  /\[object Object\]/,
  /^(Failed to fetch|Load failed|NetworkError\b|Network request failed)/i,
];

function isDeveloperText(message: string, code: string | undefined): boolean {
  if (code && (DEVELOPER_CODES.has(code) || /^http_\d{3}$/.test(code))) return true;
  return DEVELOPER_TEXT.some((re) => re.test(message));
}

function readMessage(error: unknown): { message: string; code?: string } | null {
  if (typeof error === "string" && error.trim()) return { message: error };

  if (error && typeof error === "object") {
    const obj = error as Record<string, unknown>;

    // Eden wraps the parsed response body in `.value`; our API envelope is
    // `{ error: { code, message } }`, Elysia's validation errors are flat.
    const bodies = [obj.value, obj];
    for (const body of bodies) {
      if (!body || typeof body !== "object") continue;
      const b = body as Record<string, unknown>;
      const code = typeof b.code === "string" ? b.code : undefined;
      const inner = b.error;
      if (inner && typeof inner === "object") {
        const i = inner as Record<string, unknown>;
        const m = i.message;
        const innerCode = typeof i.code === "string" ? i.code : code;
        if (typeof m === "string" && m.trim()) return { message: m, code: innerCode };
      }
      const m = b.message;
      if (typeof m === "string" && m.trim() && m !== "[object Object]") return { message: m, code };
      if (m && typeof m === "object") {
        const nested = (m as Record<string, unknown>).message;
        if (typeof nested === "string" && nested.trim()) return { message: nested, code };
      }
    }

    if (typeof obj.value === "string" && obj.value.trim()) return { message: obj.value };
  }

  return null;
}

export function unwrap<T>(response: {
  data: T | null;
  error: unknown;
}): T {
  if (response.error !== null && response.error !== undefined) {
    throw mapEdenError(response.error);
  }
  return response.data as T;
}
